/**
 * The person's script library — the same one the CRM chat window edits
 * (GET/PUT /v1/users/script-library on the CRM, one library per CRM user).
 */
import { crmFetch, dataOf } from './api';

export interface ScriptFolder {
  id: string;
  name: string;
}

export interface Script {
  id: string;
  folderId: string;
  title: string;
  text: string;
}

export interface ScriptLibraryData {
  folders: ScriptFolder[];
  scripts: Script[];
}

/** What the CRM shows before anyone saved a library. */
export const DEFAULT_LIBRARY: ScriptLibraryData = {
  folders: [
    { id: 'greetings', name: 'Greetings' },
    { id: 'engagement', name: 'Engagement' },
    { id: 'sales', name: 'Sales' },
  ],
  scripts: [
    { id: 'welcome-warm', folderId: 'greetings', title: 'Warm welcome', text: 'Hey, welcome in 💕 How’s your day going so far?' },
    { id: 'welcome-return', folderId: 'greetings', title: 'Welcome back', text: 'Hey you, good to see you again 😌 What have you been up to?' },
    { id: 'engage-interests', folderId: 'engagement', title: 'Learn their interests', text: 'Tell me something about you — what do you usually do when you want to relax?' },
    { id: 'engage-follow-up', folderId: 'engagement', title: 'Keep the conversation going', text: 'I like that answer 😏 What made you get into it?' },
    { id: 'sales-soft', folderId: 'sales', title: 'Soft content transition', text: 'You’re making me want to show you something I made earlier… want a little preview? 👀' },
  ],
};

export function normalizeLibrary(value: unknown): ScriptLibraryData | null {
  const v = (value && typeof value === 'object' ? value : null) as Record<string, unknown> | null;
  if (!v) return null;
  const folders = (Array.isArray(v.folders) ? v.folders : [])
    .map((f) => ({ id: String((f as ScriptFolder)?.id || '').trim(), name: String((f as ScriptFolder)?.name || '').trim() }))
    .filter((f) => f.id && f.name);
  const ids = new Set(folders.map((f) => f.id));
  const scripts = (Array.isArray(v.scripts) ? v.scripts : [])
    .map((s) => ({
      id: String((s as Script)?.id || '').trim(),
      folderId: String((s as Script)?.folderId || '').trim(),
      title: String((s as Script)?.title || '').trim(),
      text: String((s as Script)?.text || '').trim(),
    }))
    .filter((s) => s.id && s.title && s.text && ids.has(s.folderId));
  return folders.length ? { folders, scripts } : null;
}

export async function loadLibrary(accountId: string): Promise<ScriptLibraryData> {
  const body = await crmFetch('/v1/users/script-library', { accountId, local: true });
  return normalizeLibrary(dataOf<{ library?: unknown }>(body).library) || structuredClone(DEFAULT_LIBRARY);
}

export async function saveLibrary(accountId: string, library: ScriptLibraryData): Promise<void> {
  await crmFetch('/v1/users/script-library', { accountId, local: true, method: 'PUT', body: { library } });
}

export function newId(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}
